import type { PlayerLocationDeltas } from '@repo/types/maps/map-types';
import type { GameSessionInternal } from '@repo/types/sessions/session-types';
import type { FilterSpecification, GeoJSONSource, LineLayerSpecification, Map as MapLibreMap } from 'maplibre-gl';
import * as MapLibre from 'maplibre-gl';
import { transformTrailCoordinates } from '../utils/coordinate-transform';
import { interpolateCoordinates, interpolateTrailPoint } from '../utils/interpolation-utils';
import { getWalkBoundaryCoordinates, type TrailSegment } from '../utils/teleport-detection';
import {
    ACTIVE_SOURCE_ID,
    buildLineGradientExpression,
    COLOR_CURRENT,
    COLOR_FINISH,
    COLOR_HEAD,
    COLOR_START,
    COLOR_START_INNER,
    COLOR_STROKE,
    COLOR_TELEPORT_LINE,
    COLOR_TELEPORT_MARKER,
    COLOR_TRAIL_SOLID,
    FLAG_ICON_SIZE,
    FLAG_SVG,
    HEAD_RADIUS_MAX,
    HEAD_RADIUS_MIN,
    HEAD_RADIUS_ZOOM_MAX,
    HEAD_RADIUS_ZOOM_MIN,
    LAYER_IDS,
    LINE_WIDTH_BASE,
    LINE_WIDTH_MAX,
    LINE_WIDTH_MIN,
    LINE_WIDTH_ZOOM_MAX,
    LINE_WIDTH_ZOOM_MIN,
    OPACITY_CURRENT,
    OPACITY_FINISH,
    OPACITY_HEAD,
    OPACITY_START,
    OPACITY_TELEPORT_MARKER,
    PROP_TYPE,
    RADIUS_CURRENT,
    RADIUS_FINISH,
    RADIUS_START,
    RADIUS_START_INNER,
    RADIUS_TELEPORT_MARKER,
    STROKE_WIDTH_CURRENT,
    STROKE_WIDTH_FINISH,
    STROKE_WIDTH_HEAD,
    STROKE_WIDTH_START,
    TELEPORT_LINE_DASH,
    TELEPORT_SOURCE_ID,
    TRAIL_SOURCE_ID,
    TYPE_CURRENT,
    TYPE_FINISH,
    TYPE_HEAD,
    TYPE_START,
    TYPE_TELEPORT_LINE,
    TYPE_TELEPORT_MARKER,
    TYPE_TRAIL,
} from './trail-rendering';

export interface TrailRenderOptions {
    session: GameSessionInternal | null;
    progress: number;
    mode: 'static' | 'progressive';
    markers?: {
        start?: boolean;
        finish?: boolean;
        current?: boolean;
        head?: boolean;
    };
    autoFitBounds?: boolean;
    deltas?: PlayerLocationDeltas | null;
    segments?: TrailSegment[] | null;
    /** Pre-computed effective trail coordinates (already transformed & junk-stripped). */
    coordinates?: [number, number][];
    /** Time-based progress (0–1) from the playback scrubber, used for segment visibility. */
    timeProgress?: number;
}

interface SegmentVisibility {
    completedWalks: TrailSegment[];
    activeWalk: TrailSegment | null;
    activeProgress: number;
    visibleTeleports: TrailSegment[];
}

export class UnifiedTrailRenderer {
    private map: MapLibreMap;
    private isActive = false;
    private sourceId = TRAIL_SOURCE_ID;
    private layers = {
        trail: LAYER_IDS.TRAIL,
        startMarker: LAYER_IDS.START,
        startMarkerInner: LAYER_IDS.START_INNER,
        finishMarker: LAYER_IDS.FINISH,
        finishMarkerIcon: LAYER_IDS.FINISH_ICON,
        currentMarker: LAYER_IDS.CURRENT,
        headMarker: LAYER_IDS.HEAD,
    } as const;

    private teleportSourceId = TELEPORT_SOURCE_ID;
    private teleportLayers = {
        line: LAYER_IDS.TELEPORT_LINE,
        marker: LAYER_IDS.TELEPORT_MARKER,
    } as const;

    private activeSourceId = ACTIVE_SOURCE_ID;
    private activeLayers = {
        trail: LAYER_IDS.ACTIVE_TRAIL,
    } as const;

    private flagImageLoaded = false;
    private isDisposed = false;

    private gradientRaf: number | null = null;
    private pendingGradients: Map<string, number> = new Map();
    private pendingRenderOptions: TrailRenderOptions | null = null;
    private deferredRenderScheduled = false;
    private deferredRenderHandler: (() => void) | null = null;

    constructor(map: MapLibreMap) {
        this.map = map;
        this.initializeLayers();
        this.activate();
    }

    public activate(): void {
        if (this.isDisposed) return;
        this.isActive = true;
    }

    public deactivate(): void {
        this.isActive = false;
    }

    private scheduleDeferredRender(): void {
        if (this.isDisposed || this.deferredRenderScheduled) return;
        this.deferredRenderScheduled = true;

        const tryRender = () => {
            this.cancelDeferredRender();
            if (this.isDisposed || this.map._removed || !this.pendingRenderOptions) return;

            const pending = this.pendingRenderOptions;
            this.pendingRenderOptions = null;
            this.render(pending);
        };

        this.deferredRenderHandler = tryRender;
        this.map.once('load', tryRender);
        this.map.once('idle', tryRender);
    }

    private cancelDeferredRender(): void {
        if (this.deferredRenderHandler) {
            this.map.off('load', this.deferredRenderHandler);
            this.map.off('idle', this.deferredRenderHandler);
            this.deferredRenderHandler = null;
        }
        this.deferredRenderScheduled = false;
    }

    private loadFlagIcon(): void {
        if (this.isDisposed) return;

        try {
            if (this.flagImageLoaded || this.map.hasImage('finish-flag')) {
                this.addFlagIconLayer();
                return;
            }
        } catch {
            // Map may not be ready yet, continue with image loading
        }

        const blob = new Blob([FLAG_SVG], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const img = new Image(32, 32);

        img.onload = () => {
            URL.revokeObjectURL(url);
            if (this.isDisposed || this.map._removed) return;

            try {
                if (!this.map.hasImage('finish-flag')) {
                    this.map.addImage('finish-flag', img);
                }
                this.flagImageLoaded = true;
                this.addFlagIconLayer();
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('[TrailRenderer] Failed to add flag icon:', error);
                }
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            if (process.env.NODE_ENV === 'development') {
                console.warn('[TrailRenderer] Failed to load flag icon');
            }
        };

        img.src = url;
    }

    private addFlagIconLayer(): void {
        if (this.isDisposed || this.map._removed) return;

        try {
            if (!this.map.getLayer(this.layers.finishMarkerIcon) && this.map.hasImage('finish-flag')) {
                this.map.addLayer({
                    id: this.layers.finishMarkerIcon,
                    type: 'symbol',
                    source: this.sourceId,
                    layout: {
                        'icon-image': 'finish-flag',
                        'icon-size': FLAG_ICON_SIZE,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                    },
                    filter: ['==', ['get', PROP_TYPE], TYPE_FINISH],
                });
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[TrailRenderer] Failed to add flag icon layer:', error);
            }
        }
    }

    private initializeLayers(): boolean {
        if (this.isDisposed || this.map._removed) return false;

        const addLayers = () => {
            try {
                // Main trail source (completed segments + single-LineString fallback)
                if (!this.map.getSource(this.sourceId)) {
                    this.map.addSource(this.sourceId, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: [],
                        },
                        lineMetrics: true,
                    });
                }

                // Teleport source (dashed lines + endpoint markers)
                if (!this.map.getSource(this.teleportSourceId)) {
                    this.map.addSource(this.teleportSourceId, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: [],
                        },
                    });
                }

                // Active trail source (currently-revealing segment with line-gradient)
                if (!this.map.getSource(this.activeSourceId)) {
                    this.map.addSource(this.activeSourceId, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: [],
                        },
                        lineMetrics: true,
                    });
                }

                // Layer order (bottom to top):
                // 1. Teleport dashed line
                // 2. Main trail line (completed segments)
                // 3. Active trail line (currently-animating segment)
                // 4. Teleport endpoint markers
                // 5. Start/finish/current/head markers

                if (!this.map.getLayer(this.teleportLayers.line)) {
                    try {
                        this.map.addLayer({
                            id: this.teleportLayers.line,
                            type: 'line',
                            source: this.teleportSourceId,
                            layout: {
                                'line-cap': 'round',
                                'line-join': 'round',
                            },
                            paint: {
                                'line-color': COLOR_TELEPORT_LINE,
                                'line-dasharray': TELEPORT_LINE_DASH,
                                'line-width': [
                                    'interpolate',
                                    ['exponential', LINE_WIDTH_BASE],
                                    ['zoom'],
                                    LINE_WIDTH_ZOOM_MIN,
                                    LINE_WIDTH_MIN,
                                    LINE_WIDTH_ZOOM_MAX,
                                    LINE_WIDTH_MAX,
                                ],
                            },
                            filter: ['==', ['get', PROP_TYPE], TYPE_TELEPORT_LINE],
                        });
                    } catch (error) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('[TrailRenderer] Failed to add teleport line layer:', error);
                        }
                    }
                }

                if (!this.map.getLayer(this.layers.trail)) {
                    const filter: FilterSpecification = ['==', ['get', PROP_TYPE], TYPE_TRAIL];
                    const trailLayer: LineLayerSpecification = {
                        id: this.layers.trail,
                        type: 'line',
                        source: this.sourceId,
                        layout: {
                            'line-cap': 'round',
                            'line-join': 'round',
                        },
                        paint: {
                            'line-width': [
                                'interpolate',
                                ['exponential', LINE_WIDTH_BASE],
                                ['zoom'],
                                LINE_WIDTH_ZOOM_MIN,
                                LINE_WIDTH_MIN,
                                LINE_WIDTH_ZOOM_MAX,
                                LINE_WIDTH_MAX,
                            ],
                            'line-gradient': [
                                'interpolate',
                                ['linear'],
                                ['line-progress'],
                                0,
                                COLOR_TRAIL_SOLID,
                                1,
                                COLOR_TRAIL_SOLID,
                            ],
                        },
                        filter,
                    };

                    try {
                        this.map.addLayer(trailLayer);
                    } catch (error) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('[TrailRenderer] Failed to add trail layer:', error);
                        }
                    }
                }

                if (!this.map.getLayer(this.activeLayers.trail)) {
                    const activeTrailLayer: LineLayerSpecification = {
                        id: this.activeLayers.trail,
                        type: 'line',
                        source: this.activeSourceId,
                        layout: {
                            'line-cap': 'round',
                            'line-join': 'round',
                        },
                        paint: {
                            'line-width': [
                                'interpolate',
                                ['exponential', LINE_WIDTH_BASE],
                                ['zoom'],
                                LINE_WIDTH_ZOOM_MIN,
                                LINE_WIDTH_MIN,
                                LINE_WIDTH_ZOOM_MAX,
                                LINE_WIDTH_MAX,
                            ],
                            'line-gradient': [
                                'interpolate',
                                ['linear'],
                                ['line-progress'],
                                0,
                                COLOR_TRAIL_SOLID,
                                1,
                                COLOR_TRAIL_SOLID,
                            ],
                        },
                        filter: ['==', ['get', PROP_TYPE], TYPE_TRAIL],
                    };

                    try {
                        this.map.addLayer(activeTrailLayer);
                    } catch (error) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('[TrailRenderer] Failed to add active trail layer:', error);
                        }
                    }
                }

                if (!this.map.getLayer(this.teleportLayers.marker)) {
                    try {
                        this.map.addLayer({
                            id: this.teleportLayers.marker,
                            type: 'circle',
                            source: this.teleportSourceId,
                            paint: {
                                'circle-color': COLOR_TELEPORT_MARKER,
                                'circle-radius': RADIUS_TELEPORT_MARKER,
                                'circle-opacity': OPACITY_TELEPORT_MARKER,
                                'circle-stroke-color': COLOR_STROKE,
                            },
                            filter: ['==', ['get', PROP_TYPE], TYPE_TELEPORT_MARKER],
                        });
                    } catch (error) {
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('[TrailRenderer] Failed to add teleport marker layer:', error);
                        }
                    }
                }

                if (!this.map.getLayer(this.layers.startMarker)) {
                    this.map.addLayer({
                        id: this.layers.startMarker,
                        type: 'circle',
                        source: this.sourceId,
                        paint: {
                            'circle-color': COLOR_START,
                            'circle-radius': RADIUS_START,
                            'circle-opacity': OPACITY_START,
                            'circle-stroke-color': COLOR_STROKE,
                            'circle-stroke-width': STROKE_WIDTH_START,
                        },
                        filter: ['==', ['get', PROP_TYPE], TYPE_START],
                    });
                }

                if (!this.map.getLayer(this.layers.startMarkerInner)) {
                    this.map.addLayer({
                        id: this.layers.startMarkerInner,
                        type: 'circle',
                        source: this.sourceId,
                        paint: {
                            'circle-color': COLOR_START_INNER,
                            'circle-radius': RADIUS_START_INNER,
                            'circle-opacity': 1,
                        },
                        filter: ['==', ['get', PROP_TYPE], TYPE_START],
                    });
                }

                if (!this.map.getLayer(this.layers.finishMarker)) {
                    this.map.addLayer({
                        id: this.layers.finishMarker,
                        type: 'circle',
                        source: this.sourceId,
                        paint: {
                            'circle-color': COLOR_FINISH,
                            'circle-radius': RADIUS_FINISH,
                            'circle-opacity': OPACITY_FINISH,
                            'circle-stroke-color': COLOR_STROKE,
                            'circle-stroke-width': STROKE_WIDTH_FINISH,
                        },
                        filter: ['==', ['get', PROP_TYPE], TYPE_FINISH],
                    });
                }

                this.loadFlagIcon();

                if (!this.map.getLayer(this.layers.currentMarker)) {
                    this.map.addLayer({
                        id: this.layers.currentMarker,
                        type: 'circle',
                        source: this.sourceId,
                        paint: {
                            'circle-color': COLOR_CURRENT,
                            'circle-radius': RADIUS_CURRENT,
                            'circle-opacity': OPACITY_CURRENT,
                            'circle-stroke-color': COLOR_STROKE,
                            'circle-stroke-width': STROKE_WIDTH_CURRENT,
                        },
                        filter: ['==', ['get', PROP_TYPE], TYPE_CURRENT],
                    });
                }

                if (!this.map.getLayer(this.layers.headMarker)) {
                    this.map.addLayer({
                        id: this.layers.headMarker,
                        type: 'circle',
                        source: this.sourceId,
                        paint: {
                            'circle-color': COLOR_HEAD,
                            'circle-radius': [
                                'interpolate',
                                ['exponential', LINE_WIDTH_BASE],
                                ['zoom'],
                                HEAD_RADIUS_ZOOM_MIN,
                                HEAD_RADIUS_MIN,
                                HEAD_RADIUS_ZOOM_MAX,
                                HEAD_RADIUS_MAX,
                            ],
                            'circle-stroke-color': COLOR_STROKE,
                            'circle-stroke-width': STROKE_WIDTH_HEAD,
                            'circle-opacity': OPACITY_HEAD,
                        },
                        filter: ['==', ['get', PROP_TYPE], TYPE_HEAD],
                    });
                }
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('[TrailRenderer] Failed to initialize layers:', error);
                }
            }
        };

        addLayers();
        return !!this.map.getSource(this.sourceId) && !!this.map.getLayer(this.layers.trail);
    }

    public render(options: TrailRenderOptions) {
        const { session, autoFitBounds = false, deltas, segments, coordinates } = options;

        if (this.isDisposed) return;

        if (!session?.trail.length || this.map._removed) {
            this.clear();
            return;
        }

        if (!this.map.isStyleLoaded()) {
            this.pendingRenderOptions = options;
            this.scheduleDeferredRender();
            return;
        }

        if (!this.isActive) {
            this.activate();
        }

        const effectiveCoords =
            coordinates ??
            (deltas
                ? transformTrailCoordinates(session, deltas)
                : session.trail.map(point => [point.x, point.y] as [number, number]));

        if (!this.map.getSource(this.sourceId) || !this.map.getLayer(this.layers.trail)) {
            if (!this.initializeLayers()) {
                this.pendingRenderOptions = options;
                return;
            }
        }

        if (segments && segments.length > 0) {
            this.renderWithSegments(options, segments, effectiveCoords);
        } else {
            this.renderSingleTrail(options, effectiveCoords);
        }

        if (autoFitBounds && effectiveCoords.length > 1) {
            if (segments && segments.length > 0) {
                const boundsCoords = segments.filter(s => s.type === 'walk').flatMap(s => s.coordinates);
                if (boundsCoords.length > 1) {
                    this.fitBounds(boundsCoords);
                }
            } else {
                this.fitBounds(effectiveCoords);
            }
        }
    }

    /**
     * Single-LineString render path used when no teleport segments are detected.
     */
    private renderSingleTrail(options: TrailRenderOptions, allCoordinates: [number, number][]) {
        const { session, progress, mode, markers = {}, deltas } = options;
        if (!session) return;

        const features: GeoJSON.Feature[] = [];

        features.push({
            type: 'Feature',
            properties: { [PROP_TYPE]: TYPE_TRAIL, progress: mode === 'progressive' ? progress : 1 },
            geometry: {
                type: 'LineString',
                coordinates: allCoordinates,
            },
        });

        if (mode === 'progressive' && markers.head !== false && progress > 0) {
            const headPosition = interpolateCoordinates(allCoordinates, progress);
            if (headPosition) {
                features.push({
                    type: 'Feature',
                    properties: { [PROP_TYPE]: TYPE_HEAD },
                    geometry: {
                        type: 'Point',
                        coordinates: headPosition,
                    },
                });
            }
        }

        this.addMarkerFeatures(features, session, allCoordinates, mode, progress, markers, deltas ?? null);

        this.updateSource(this.sourceId, features);
        this.clearSource(this.activeSourceId);
        this.clearSource(this.teleportSourceId);

        const reveal = mode === 'progressive' ? progress : 1;
        this.scheduleGradientUpdate(reveal, this.layers.trail);
    }

    /**
     * Segment-based render path for trails with teleport detection.
     * Splits the trail into completed walks, an active walk, and teleport dashes.
     */
    private renderWithSegments(
        options: TrailRenderOptions,
        segments: TrailSegment[],
        allCoordinates: [number, number][],
    ) {
        const { session, progress, mode, markers = {}, deltas, timeProgress } = options;
        if (!session) return;

        // Use time-based progress for segment visibility so that teleport time
        // gaps are accounted for. Distance-based progress skips teleport gaps
        // (zero distance), causing the trail to finish before the scrubber.
        const visibilityProgress = mode === 'progressive' ? (timeProgress ?? progress) : 1;
        const visibility = this.computeSegmentVisibility(segments, visibilityProgress, allCoordinates.length);

        const mainFeatures: GeoJSON.Feature[] = [];
        for (const seg of visibility.completedWalks) {
            mainFeatures.push({
                type: 'Feature',
                properties: { [PROP_TYPE]: TYPE_TRAIL },
                geometry: {
                    type: 'LineString',
                    coordinates: seg.coordinates,
                },
            });
        }

        const activeFeatures: GeoJSON.Feature[] = [];
        if (visibility.activeWalk) {
            activeFeatures.push({
                type: 'Feature',
                properties: { [PROP_TYPE]: TYPE_TRAIL },
                geometry: {
                    type: 'LineString',
                    coordinates: visibility.activeWalk.coordinates,
                },
            });
        }

        const teleportFeatures: GeoJSON.Feature[] = [];
        for (const seg of visibility.visibleTeleports) {
            const startCoord = seg.coordinates[0];
            const endCoord = seg.coordinates[seg.coordinates.length - 1];

            if (startCoord && endCoord) {
                teleportFeatures.push({
                    type: 'Feature',
                    properties: { [PROP_TYPE]: TYPE_TELEPORT_LINE },
                    geometry: { type: 'LineString', coordinates: [startCoord, endCoord] },
                });
            }

            if (startCoord) {
                teleportFeatures.push({
                    type: 'Feature',
                    properties: { [PROP_TYPE]: TYPE_TELEPORT_MARKER },
                    geometry: { type: 'Point', coordinates: startCoord },
                });
            }
            if (endCoord) {
                teleportFeatures.push({
                    type: 'Feature',
                    properties: { [PROP_TYPE]: TYPE_TELEPORT_MARKER },
                    geometry: { type: 'Point', coordinates: endCoord },
                });
            }
        }

        if (mode === 'progressive' && markers.head !== false && visibilityProgress > 0) {
            const headPosition = this.computeHeadPosition(visibility, allCoordinates, visibilityProgress);
            if (headPosition) {
                mainFeatures.push({
                    type: 'Feature',
                    properties: { [PROP_TYPE]: TYPE_HEAD },
                    geometry: {
                        type: 'Point',
                        coordinates: headPosition,
                    },
                });
            }
        }

        const { start: segStartCoord, end: segEndCoord } = getWalkBoundaryCoordinates(segments, allCoordinates);

        this.addMarkerFeatures(
            mainFeatures,
            session,
            allCoordinates,
            mode,
            visibilityProgress,
            markers,
            deltas ?? null,
            segStartCoord,
            segEndCoord,
        );

        this.updateSource(this.sourceId, mainFeatures);
        this.updateSource(this.activeSourceId, activeFeatures);
        this.updateSource(this.teleportSourceId, teleportFeatures);

        this.scheduleGradientUpdate(1, this.layers.trail);

        if (visibility.activeWalk) {
            this.scheduleGradientUpdate(visibility.activeProgress, this.activeLayers.trail);
        } else {
            this.scheduleGradientUpdate(0, this.activeLayers.trail);
        }
    }

    private computeHeadPosition(
        visibility: SegmentVisibility,
        allCoordinates: [number, number][],
        progress: number,
    ): [number, number] | null {
        if (visibility.activeWalk && visibility.activeProgress > 0) {
            return interpolateCoordinates(visibility.activeWalk.coordinates, visibility.activeProgress);
        }

        // If no active walk, head is at end of last completed walk
        const lastCompleted = visibility.completedWalks[visibility.completedWalks.length - 1];
        if (lastCompleted) {
            return lastCompleted.coordinates[lastCompleted.coordinates.length - 1] ?? null;
        }

        return interpolateCoordinates(allCoordinates, progress);
    }

    private addMarkerFeatures(
        features: GeoJSON.Feature[],
        session: GameSessionInternal,
        allCoordinates: [number, number][],
        mode: 'static' | 'progressive',
        progress: number,
        markers: TrailRenderOptions['markers'],
        deltas: PlayerLocationDeltas | null,
        startOverride?: [number, number],
        endOverride?: [number, number],
    ) {
        const startCoordinate = startOverride ?? allCoordinates[0];
        const endCoordinate = endOverride ?? allCoordinates[allCoordinates.length - 1];

        if (markers?.start !== false && startCoordinate) {
            features.push({
                type: 'Feature',
                properties: { [PROP_TYPE]: TYPE_START, sessionUuid: session.uuid },
                geometry: {
                    type: 'Point',
                    coordinates: startCoordinate,
                },
            });
        }

        if (markers?.finish !== false && endCoordinate && allCoordinates.length > 1) {
            features.push({
                type: 'Feature',
                properties: { [PROP_TYPE]: TYPE_FINISH, sessionUuid: session.uuid },
                geometry: {
                    type: 'Point',
                    coordinates: endCoordinate,
                },
            });
        }

        if (markers?.current !== false && session.trail.length > 0) {
            const lastTrailPoint = session.trail[session.trail.length - 1];
            const currentPosition =
                mode === 'progressive' ? this.interpolatePosition(session, progress) : lastTrailPoint;

            if (currentPosition) {
                const currentCoords = deltas
                    ? transformTrailCoordinates({ ...session, trail: [currentPosition] }, deltas)[0]
                    : ([currentPosition.x, currentPosition.y] as [number, number]);

                if (currentCoords) {
                    features.push({
                        type: 'Feature',
                        properties: { [PROP_TYPE]: TYPE_CURRENT, sessionUuid: session.uuid },
                        geometry: {
                            type: 'Point',
                            coordinates: currentCoords,
                        },
                    });
                }
            }
        }
    }

    /**
     * Compute which segments are completed, active, or visible based on overall progress.
     * Uses index-based fractions (coordinate index / total count) so that teleport
     * gaps consume proportional progress, matching the time-based scrubber.
     */
    private computeSegmentVisibility(
        segments: TrailSegment[],
        progress: number,
        totalCoordCount: number,
    ): SegmentVisibility {
        if (progress <= 0) {
            return { completedWalks: [], activeWalk: null, activeProgress: 0, visibleTeleports: [] };
        }

        if (progress >= 1) {
            return {
                completedWalks: segments.filter(s => s.type === 'walk'),
                activeWalk: null,
                activeProgress: 0,
                visibleTeleports: segments.filter(s => s.type === 'teleport'),
            };
        }

        const indexToProgress = (idx: number) => (totalCoordCount > 1 ? idx / (totalCoordCount - 1) : 0);

        const completedWalks: TrailSegment[] = [];
        const visibleTeleports: TrailSegment[] = [];
        let activeWalk: TrailSegment | null = null;
        let activeProgress = 0;

        let foundActive = false;
        for (const seg of segments) {
            if (seg.type === 'teleport') {
                if (indexToProgress(seg.startIndex) <= progress) {
                    visibleTeleports.push(seg);
                }
                continue;
            }

            if (!foundActive) {
                const segStartProgress = indexToProgress(seg.startIndex);
                const segEndProgress = indexToProgress(seg.endIndex);

                if (segEndProgress <= progress) {
                    completedWalks.push(seg);
                } else if (segStartProgress <= progress) {
                    activeWalk = seg;
                    const segRange = segEndProgress - segStartProgress;
                    activeProgress = segRange > 0 ? (progress - segStartProgress) / segRange : 1;
                    activeProgress = Math.max(0, Math.min(1, activeProgress));
                    foundActive = true;
                } else {
                    foundActive = true;
                }
            }
        }

        return { completedWalks, activeWalk, activeProgress, visibleTeleports };
    }

    private updateSource(sourceId: string, features: GeoJSON.Feature[]) {
        try {
            const source = this.map.getSource(sourceId) as GeoJSONSource;
            if (source) {
                source.setData({
                    type: 'FeatureCollection',
                    features,
                });
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`[TrailRenderer] Failed to update source ${sourceId}:`, error);
            }
        }
    }

    private clearSource(sourceId: string) {
        try {
            const source = this.map.getSource(sourceId) as GeoJSONSource;
            if (source) {
                source.setData({
                    type: 'FeatureCollection',
                    features: [],
                });
            }
        } catch {
            // Source may not exist yet, ignore
        }
    }

    /** Batches line-gradient updates across layers into a single animation frame. */
    private scheduleGradientUpdate(reveal: number, layerId: string) {
        if (this.isDisposed || this.map._removed) return;

        this.pendingGradients.set(layerId, Math.max(0, Math.min(1, reveal)));
        if (this.gradientRaf == null) {
            this.gradientRaf = requestAnimationFrame(() => {
                try {
                    for (const [id, value] of this.pendingGradients) {
                        if (this.map.getLayer(id)) {
                            const expr = buildLineGradientExpression(value);
                            this.map.setPaintProperty(id, 'line-gradient', expr);
                        }
                    }
                    this.pendingGradients.clear();
                } finally {
                    this.gradientRaf = null;
                }
            });
        }
    }

    private interpolatePosition(session: GameSessionInternal, progress: number) {
        if (!session?.trail.length) return null;
        return interpolateTrailPoint(session.trail, progress);
    }

    public fitBounds(coordinates: [number, number][]) {
        try {
            if (coordinates.length === 0) return;

            const bounds = coordinates.reduce((bounds, coord) => {
                return bounds.extend(coord);
            }, new MapLibre.LngLatBounds(coordinates[0], coordinates[0]));

            this.map.fitBounds(bounds, {
                padding: { top: 80, right: 80, bottom: 80, left: 80 },
                duration: 1000,
                maxZoom: 16,
            });
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[TrailRenderer] Failed to fit bounds:', error);
            }
        }
    }

    public clear() {
        if (this.isDisposed) return;

        this.pendingRenderOptions = null;
        this.cancelDeferredRender();
        this.clearSource(this.sourceId);
        this.clearSource(this.activeSourceId);
        this.clearSource(this.teleportSourceId);
    }

    public cleanup() {
        this.isDisposed = true;
        this.pendingRenderOptions = null;
        this.cancelDeferredRender();
        if (this.gradientRaf != null) {
            cancelAnimationFrame(this.gradientRaf);
            this.gradientRaf = null;
            this.pendingGradients.clear();
        }

        if (!this.map || this.map._removed) return;

        try {
            // Remove layers in reverse order (top to bottom)
            Object.values(this.layers)
                .reverse()
                .forEach(layerId => {
                    if (this.map.getLayer(layerId)) {
                        this.map.removeLayer(layerId);
                    }
                });

            Object.values(this.teleportLayers)
                .reverse()
                .forEach(layerId => {
                    if (this.map.getLayer(layerId)) {
                        this.map.removeLayer(layerId);
                    }
                });

            if (this.map.getLayer(this.activeLayers.trail)) {
                this.map.removeLayer(this.activeLayers.trail);
            }

            if (this.map.getSource(this.sourceId)) {
                this.map.removeSource(this.sourceId);
            }
            if (this.map.getSource(this.teleportSourceId)) {
                this.map.removeSource(this.teleportSourceId);
            }
            if (this.map.getSource(this.activeSourceId)) {
                this.map.removeSource(this.activeSourceId);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[TrailRenderer] Failed to cleanup layers:', error);
            }
        }
    }
}
